import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ManualDemandEntry } from "../../components/ManualDemandEntry";
import { ThirdPartyEntry } from "./ThirdPartyEntry";
import UploadStatus from "./UploadStatus";
import { api, errMsg, fmtDate, fmtINR, downloadBlob } from "../../lib/api";
import { UploadPreviewTable } from "../../components/UploadPreviewTable";
import { AreaFlagsBanner } from "../../components/AreaFlagsBanner";
import { PaymentImportSection } from "../../components/PaymentImportSection";
import { BatchHistorySection } from "../../components/BatchHistory";
import { Checkbox } from "../../components/ui/checkbox";
import { BatchLogSelectionBar, DeleteBatchLogsDialog } from "../../components/DeleteBatchLogsDialog";
import { deleteBatchLogs, useBatchLogAccess } from "../../lib/batchLogs";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice, ViewOnlyBadge } from "../../components/FeatureGate";

// Phase 152: Demand Management tabs <-> "upload" module sub-features
const TABS = [
  { value: "bulk", feature: "bulk_excel", label: "Bulk Excel Upload", testId: "tab-bulk-upload" },
  { value: "manual", feature: "manual_entry", label: "Manual Entry", testId: "tab-manual-entry" },
  { value: "third_party", feature: "third_party", label: "Third Party", testId: "tab-third-party" },
  { value: "payments", feature: "import_payments", label: "Import Payments", testId: "tab-import-payments" },
  { value: "status", feature: "upload_status", label: "Upload Status", testId: "tab-upload-status" },
  { value: "history", feature: "batch_history", label: "Batch History", testId: "tab-batch-history" },
];

const STEPS = ["Upload", "Map Columns", "Validate", "Process", "Done"];

export default function DemandUpload() {
  const { code } = useParams();
  const [step, setStep] = useState(0);
  const [fys, setFys] = useState([]);
  const [fyId, setFyId] = useState("");
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [targets, setTargets] = useState([]);
  const [demandTargets, setDemandTargets] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [validation, setValidation] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());   // Phase 101: rows ticked in the preview
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [autoCreate, setAutoCreate] = useState(true);
  // Phase 151: remove upload records from the "Upload history" card (log entry only — imported data stays)
  const { canDelete: canDeleteLogs } = useBatchLogAccess(code);
  const [logSelected, setLogSelected] = useState(() => new Set());
  const [logDeleteOpen, setLogDeleteOpen] = useState(false);

  // Phase 152: sub-feature access — hidden tabs when "No access", read-only tabs when "View only"
  const access = useRoleModules(code);
  const visibleTabs = TABS.filter((t) => access.featureAllowed("upload", t.feature));
  const [tab, setTab] = useState("bulk");
  useEffect(() => {
    if (!access.loaded) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "upload", TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length]);
  const canBulk = access.canWrite("upload", "bulk_excel");

  const loadHistory = () => api.get(`/t/${code}/uploads`).then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/t/${code}/masters/financial-years`).then((r) => {
      setFys(r.data);
      const cur = r.data.find((f) => f.is_current);
      if (cur) setFyId(cur.id);
    }).catch(() => {});
    loadHistory();
    // eslint-disable-next-line
  }, [code]);

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/uploads/template`, "demand-bulk-entry-template.xlsx");
      toast.success("Template downloaded — one row per floor, columns match the Manual Entry form");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const doUpload = async () => {
    if (!file || !fyId) return toast.error("Select a file and financial year");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("financial_year_id", fyId);
      const r = await api.post(`/t/${code}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(r.data.batch);
      setMapping(r.data.suggested_mapping);
      setTargets(r.data.available_targets);
      setDemandTargets(r.data.demand_targets);
      setSampleRows(r.data.sample_rows);
      setStep(1);
      toast.success(`Parsed ${r.data.batch.total_records} rows. Columns auto-detected.`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doValidate = async () => {
    setBusy(true);
    try {
      const custom_fields = Object.values(mapping).filter((t) => t?.startsWith("custom:")).map((t) => ({ field_name: t.split(":")[1] }));
      const r = await api.post(`/t/${code}/uploads/${batch.id}/validate`, { mapping, custom_fields, auto_create_masters: autoCreate });
      setValidation(r.data);
      // default: every processable (valid + warning) row is ticked; error rows are locked out
      setSelectedRows(new Set((r.data.rows || []).filter((x) => x.status !== "error").map((x) => x.row_number)));
      setStep(2);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doProcess = async () => {
    if (selectedRows.size === 0) return toast.error("Select at least one row to update");
    setBusy(true);
    setStep(3);
    try {
      const r = await api.post(`/t/${code}/uploads/${batch.id}/process`, { selected_rows: [...selectedRows] });
      setSummary(r.data.summary);
      setStep(4);
      toast.success("Demand upload processed successfully");
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err));
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0); setFile(null); setBatch(null); setMapping({}); setValidation(null); setSummary(null); setSelectedRows(new Set());
  };

  // Phase 101: what Confirm will do with the current ticks
  const previewRows = validation?.rows || [];
  const [flagFilter, setFlagFilter] = useState(null);   // Phase 161: banner "Show rows" → preview filter
  const pickedRows = previewRows.filter((r) => selectedRows.has(r.row_number));
  const pickedCreate = pickedRows.filter((r) => r.action === "create").length;
  const pickedUpdate = pickedRows.length - pickedCreate;
  const pickedAmount = pickedRows.reduce((s, r) => s + (r.demand_total || 0), 0);
  const processable = previewRows.filter((r) => r.status !== "error").length;

  const setColTarget = (header, target) => setMapping({ ...mapping, [header]: target });

  const customName = (header) => {
    const cur = mapping[header];
    return cur?.startsWith("custom:") ? cur.split(":")[1] : "";
  };

  return (
    <div>
      <PageHeader title="Demand Management" subtitle="Import demands in bulk from Excel/CSV or enter them manually" />

      {access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Demand Management" testId="upload-no-access" /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={t.testId}>
              {t.label}{!access.canWrite("upload", t.feature) && <ViewOnlyBadge />}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="payments">
          <PaymentImportSection code={code} scope="main" readOnly={!access.canWrite("upload", "import_payments")} />
        </TabsContent>

        <TabsContent value="status">
          <UploadStatus code={code} readOnly={!access.canWrite("upload", "upload_status")} />
        </TabsContent>

        {/* Phase 145: unified history of Bulk Excel Upload / Bulk Entry / Bulk Update batches with 24h Undo */}
        <TabsContent value="history">
          <BatchHistorySection code={code} readOnly={!access.canWrite("upload", "batch_history")} />
        </TabsContent>

        <TabsContent value="manual">
          <ManualDemandEntry code={code} readOnly={!access.canWrite("upload", "manual_entry")} />
        </TabsContent>

        <TabsContent value="third_party">
          <ThirdPartyEntry code={code} readOnly={!access.canWrite("upload", "third_party")} />
        </TabsContent>

        <TabsContent value="bulk">
      {!canBulk && <ReadOnlyNotice what="the upload history" testId="bulk-upload-readonly-notice" className="mb-4" />}

      {/* Stepper */}
      <div className="flex items-center gap-1 sm:gap-2 mb-6 overflow-x-auto" data-testid="upload-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              i < step ? "bg-emerald-50 text-emerald-700" : i === step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              <span className="h-4 w-4 rounded-full bg-white/30 flex items-center justify-center text-[10px]">{i + 1}</span>
              {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-4 sm:w-8 bg-border shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <div className={`grid grid-cols-1 ${canBulk ? "lg:grid-cols-2" : ""} gap-4`}>
          {canBulk && <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Upload demand file</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Financial Year</Label>
                <Select value={fyId} onValueChange={setFyId}>
                  <SelectTrigger data-testid="upload-fy-select"><SelectValue placeholder="Select FY" /></SelectTrigger>
                  <SelectContent>
                    {fys.map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}{f.is_current ? " (current)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
                <FileSpreadsheet className="h-10 w-10 text-muted-foreground mb-2" />
                <p className="text-sm font-medium">{file ? file.name : "Click to choose Excel/CSV file"}</p>
                <p className="text-xs text-muted-foreground mt-1">Columns like PIN, Owner Name, Zone, Ward, Tax amounts will be auto-detected</p>
                <Input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="demand-upload-file-input" />
              </label>
              <Button className="w-full" onClick={doUpload} disabled={busy || !file} data-testid="demand-upload-next-button">
                <Upload className="h-4 w-4 mr-2" /> {busy ? "Parsing..." : "Upload & Detect Columns"}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={downloadTemplate} data-testid="demand-template-download-button">
                <Download className="h-4 w-4 mr-2" /> Download bulk entry template
              </Button>
              <p className="text-[11px] text-muted-foreground text-center -mt-2">
                Template columns mirror the Manual Entry form — one row per floor (repeat PIN/owner for multi-floor properties)
              </p>
            </CardContent>
          </Card>}

          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
              <CardTitle className="text-base font-heading">Upload history</CardTitle>
              {canDeleteLogs && history.length > 0 && (
                <div className="flex items-center gap-2">
                  {(() => {
                    const shown = history.slice(0, 8).filter((h) => h.log_deletable);
                    const all = shown.length > 0 && shown.every((h) => logSelected.has(h.id));
                    const some = shown.some((h) => logSelected.has(h.id));
                    return (
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox checked={all ? true : some ? "indeterminate" : false} disabled={shown.length === 0}
                          onCheckedChange={(on) => setLogSelected((prev) => { const n = new Set(prev); shown.forEach((h) => { if (on === true) n.add(h.id); else n.delete(h.id); }); return n; })}
                          aria-label="Select all uploads shown" data-testid="upload-history-select-all" />
                        Select all
                      </label>
                    );
                  })()}
                  <BatchLogSelectionBar count={logSelected.size} onClear={() => setLogSelected(new Set())} onDelete={() => setLogDeleteOpen(true)} testId="upload-history" />
                </div>
              )}
            </CardHeader>
            <CardContent>
              {history.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No uploads yet</p> : (
                <div className="space-y-2" data-testid="upload-history-list">
                  {history.slice(0, 8).map((h) => (
                    <div key={h.id} className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 ${logSelected.has(h.id) ? "bg-primary/5 border-primary/30" : ""}`}
                      data-testid={`upload-history-row-${h.id}`} data-state={logSelected.has(h.id) ? "selected" : undefined}>
                      {canDeleteLogs && (
                        <Checkbox checked={logSelected.has(h.id)} disabled={!h.log_deletable}
                          onCheckedChange={(on) => setLogSelected((prev) => { const n = new Set(prev); if (on === true) n.add(h.id); else n.delete(h.id); return n; })}
                          aria-label={`Select upload ${h.original_filename}`} title={h.log_deletable ? undefined : "Still processing — cannot be removed yet"}
                          data-testid={`upload-history-select-${h.id}`} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{h.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{h.fy_label} • {h.total_records} rows • {fmtDate(h.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <StatusBadge status={h.status} />
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Download uploaded file"
                          onClick={() => downloadBlob(`/t/${code}/uploads/${h.id}/file`, h.original_filename || "upload.xlsx")
                            .catch(() => toast.error("Could not download file"))}
                          data-testid={`upload-file-download-${h.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {canDeleteLogs && (
        <DeleteBatchLogsDialog open={logDeleteOpen} onOpenChange={setLogDeleteOpen}
          items={[...logSelected].map((id) => ({ kind: "excel", id, filename: history.find((h) => h.id === id)?.original_filename }))}
          request={(its) => deleteBatchLogs(code, its)} onDone={() => { setLogSelected(new Set()); loadHistory(); }} testId="upload-history-delete" />
      )}

      {step === 1 && batch && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Map columns ({batch.total_records} rows)</CardTitle>
            <p className="text-sm text-muted-foreground">Review auto-detected mapping. Map unmatched columns to fields, demand heads, or create custom fields.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table data-testid="demand-upload-mapping-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Excel Column</TableHead>
                    <TableHead>Sample Values</TableHead>
                    <TableHead className="min-w-[220px]">Maps To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batch.headers.map((h) => (
                    <TableRow key={h}>
                      <TableCell className="font-medium">{h}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {sampleRows.map((r) => r[h]).filter(Boolean).slice(0, 3).join(", ") || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 items-center flex-wrap">
                          <Select value={mapping[h]?.startsWith("custom:") ? "__custom__" : (mapping[h] || "ignore")}
                            onValueChange={(v) => setColTarget(h, v === "__custom__" ? `custom:${h.toLowerCase().replace(/[^a-z0-9]+/g, "_")}` : v)}>
                            <SelectTrigger className="w-56" data-testid={`mapping-select-${h}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-72">
                              <SelectItem value="ignore">— Ignore —</SelectItem>
                              <SelectItem value="__custom__">+ Custom field</SelectItem>
                              {targets.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                              {demandTargets.map((t) => {
                                const [, codeT, comp] = t.split(":");
                                return <SelectItem key={t} value={t}>{`Demand: ${codeT} ${comp}`}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                          {mapping[h]?.startsWith("custom:") && (
                            <Input className="w-40" value={customName(h)} placeholder="field name"
                              onChange={(e) => setColTarget(h, `custom:${e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`)} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
              <label className="flex items-start gap-2 text-sm cursor-pointer max-w-xl">
                <input type="checkbox" className="mt-0.5" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)}
                  data-testid="auto-create-masters-checkbox" />
                <span>
                  <span className="font-medium">Auto-create missing master values</span>
                  <span className="block text-xs text-muted-foreground">
                    Zones, wards, usage types, floors etc. found in the file but not in your masters will be
                    created automatically and linked (recommended for first import). You can rename them later in Masters.
                  </span>
                </span>
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset}>Cancel</Button>
                <Button onClick={doValidate} disabled={busy} data-testid="demand-upload-validate-button">
                  {busy ? "Validating..." : "Validate Data"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && validation && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Validation results</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-4 text-center">
                <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                <p className="text-2xl font-semibold text-emerald-700 tabular-nums">{validation.valid_rows}</p>
                <p className="text-xs text-emerald-700">Valid rows</p>
              </div>
              <div className="rounded-xl border bg-amber-50 border-amber-200 p-4 text-center">
                <AlertTriangle className="h-5 w-5 mx-auto text-amber-600 mb-1" />
                <p className="text-2xl font-semibold text-amber-700 tabular-nums">{validation.warning_rows}</p>
                <p className="text-xs text-amber-700">Rows with warnings</p>
              </div>
              <div className="rounded-xl border bg-red-50 border-red-200 p-4 text-center">
                <XCircle className="h-5 w-5 mx-auto text-red-600 mb-1" />
                <p className="text-2xl font-semibold text-red-700 tabular-nums">{validation.error_rows}</p>
                <p className="text-xs text-red-700">Rows with errors (skipped)</p>
              </div>
            </div>
            {/* Phase 161: zone / ward / mohalla linkage flags for this file */}
            {validation.area_flags?.rows > 0 && (
              <div className="mb-4">
                <AreaFlagsBanner summary={validation.area_flags} total={validation.total_rows} onFilter={(code) => setFlagFilter(code)} />
              </div>
            )}
            {/* Phase 101: preview + row selection before Confirm */}
            {previewRows.length > 0 ? (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold">Preview &amp; select rows</h3>
                    <p className="text-xs text-muted-foreground">Filter by zone, ward or mohalla and untick anything that should not be updated. Rows with errors are locked and always skipped.</p>
                  </div>
                </div>
                <UploadPreviewTable rows={previewRows} selected={selectedRows} onChange={setSelectedRows}
                  flagFilter={flagFilter} onFlagFilterChange={setFlagFilter} />
              </div>
            ) : validation.details.length > 0 && (
              <div className="border rounded-lg max-h-72 overflow-y-auto mb-4" data-testid="validation-details">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Owner / PIN</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validation.details.map((d) => (
                      <TableRow key={d.row_number}>
                        <TableCell className="font-mono text-xs">{d.row_number}</TableCell>
                        <TableCell className="text-sm">{d.owner || d.pin || "-"}</TableCell>
                        <TableCell>
                          {d.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
                          {d.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600">{w}</p>)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3" data-testid="upload-confirm-bar">
              <div className="text-sm" data-testid="upload-selection-summary">
                <b className="tabular-nums">{selectedRows.size}</b> of {processable} processable rows selected
                {previewRows.length > 0 && (
                  <span className="text-xs text-muted-foreground"> · {pickedUpdate} update · {pickedCreate} create · {fmtINR(pickedAmount)} demand
                    {processable - selectedRows.size > 0 ? ` · ${processable - selectedRows.size} deselected` : ""}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="demand-upload-back-mapping">Back to mapping</Button>
                <Button onClick={doProcess} disabled={busy || selectedRows.size === 0} data-testid="demand-upload-process-button">
                  Confirm — update {selectedRows.size} selected row{selectedRows.size === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && <Spinner label="Processing rows, creating properties and demands..." />}

      {step === 4 && summary && (
        <Card className="shadow-sm" data-testid="demand-upload-results-summary">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <h2 className="text-xl font-semibold font-heading">Upload processed successfully</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-2xl mx-auto">
              {[
                ["Properties created", summary.created_properties, "summary-created"],
                ["Properties updated", summary.updated_properties, "summary-updated"],
                ["Demands created", summary.created_demands, "summary-demands"],
                ["Rows skipped (errors)", summary.skipped_rows, "summary-skipped"],
              ].map(([l, v, tid]) => (
                <div key={l} className="rounded-xl border p-4">
                  <p className="text-2xl font-semibold tabular-nums" data-testid={tid}>{v}</p>
                  <p className="text-xs text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
            {summary.deselected_rows > 0 && (
              <p className="mt-4 text-sm text-muted-foreground" data-testid="summary-deselected">
                <b className="text-foreground tabular-nums">{summary.deselected_rows}</b> row{summary.deselected_rows === 1 ? " was" : "s were"} deselected in the preview and left untouched.
              </p>
            )}
            <Button className="mt-6" onClick={reset} data-testid="upload-another-button">
              <RotateCcw className="h-4 w-4 mr-2" /> Upload another file
            </Button>
          </CardContent>
        </Card>
      )}
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
